import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, CalendarDays, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard({ token, user, onLogout }) {
  usePageTitle('Dashboard');
  const [stats, setStats] = useState({
    calendars: 0,
    events: 0,
    upcomingEvents: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [calendarsRes, eventsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/calendars`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (calendarsRes.ok && eventsRes.ok) {
        const calendars = await calendarsRes.json();
        const events = await eventsRes.json();
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = events.filter(e => new Date(e.date) >= today);

        setStats({
          calendars: calendars.length,
          events: events.length,
          upcomingEvents: upcoming.length
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Bem-vindo(a) de volta, {user?.nome}!</p>
      </div>

      <div className="calendar-grid">
        <Card data-testid="stats-calendars">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Calendários</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.calendars}</div>
            <p className="text-xs text-muted-foreground">Total de calendários</p>
          </CardContent>
        </Card>

        <Card data-testid="stats-events">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Eventos</CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.events}</div>
            <p className="text-xs text-muted-foreground">Total de eventos</p>
          </CardContent>
        </Card>

        <Card data-testid="stats-upcoming">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Próximos Eventos</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.upcomingEvents}</div>
            <p className="text-xs text-muted-foreground">Eventos futuros</p>
          </CardContent>
        </Card>
      </div>

      <div style={{ marginTop: '3rem', padding: '2rem', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>Bem-vindo ao CoAgenda</h2>
        <p style={{ color: '#64748b', lineHeight: '1.6' }}>
          O CoAgenda é uma plataforma de agendas colaborativas onde você pode:
        </p>
        <ul style={{ marginTop: '1rem', marginLeft: '1.5rem', color: '#64748b', lineHeight: '1.8' }}>
          <li>Criar e gerenciar múltiplos calendários</li>
          <li>Compartilhar calendários com outros usuários</li>
          <li>Criar eventos com detalhes completos</li>
          <li>Relacionar eventos entre si</li>
          <li>Controlar permissões de leitura e escrita</li>
        </ul>
      </div>
    </Layout>
  );
}
